/**
 * Unified one-button press/release input, plus "agitation" for scrub notes:
 * pointer wiggling while held, or mashing any extra key while holding
 * Space/Enter. Agitation is accumulated and drained once per frame.
 */
export default class Input {
  constructor(canvas) {
    this.canvas = canvas;
    this._events = [];
    this._bound = false;
    this._pointerHeld = false;
    this._keysHeld = new Set();
    this._agitation = 0;
    this._lastX = 0;
    this._lastY = 0;

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onContext = (event) => event.preventDefault();
  }

  bind() {
    if (this._bound) return;
    this._bound = true;
    this.canvas.addEventListener('pointerdown', this._onPointerDown);
    this.canvas.addEventListener('pointerup', this._onPointerUp);
    this.canvas.addEventListener('pointercancel', this._onPointerUp);
    this.canvas.addEventListener('pointermove', this._onPointerMove);
    this.canvas.addEventListener('contextmenu', this._onContext);
    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
  }

  unbind() {
    if (!this._bound) return;
    this._bound = false;
    this.canvas.removeEventListener('pointerdown', this._onPointerDown);
    this.canvas.removeEventListener('pointerup', this._onPointerUp);
    this.canvas.removeEventListener('pointercancel', this._onPointerUp);
    this.canvas.removeEventListener('pointermove', this._onPointerMove);
    this.canvas.removeEventListener('contextmenu', this._onContext);
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    this._events.length = 0;
    this._pointerHeld = false;
    this._keysHeld.clear();
    this._agitation = 0;
  }

  consumeEvents() {
    if (!this._events.length) return [];
    const events = this._events.slice();
    this._events.length = 0;
    return events;
  }

  /** Agitation units accumulated since the last call (scrub fuel). */
  consumeAgitation() {
    const units = this._agitation;
    this._agitation = 0;
    return units;
  }

  _push(type, source) {
    this._events.push({ type, source, at: performance.now() });
  }

  _onPointerDown(event) {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    if (this._pointerHeld) return;
    const wasHeld = this._pointerHeld || this._keysHeld.size > 0;
    this._pointerHeld = true;
    this._lastX = event.clientX;
    this._lastY = event.clientY;
    try {
      this.canvas.setPointerCapture(event.pointerId);
    } catch {
      /* Pointer capture is optional. */
    }
    if (!wasHeld) this._push('press', 'pointer');
  }

  _onPointerUp(event) {
    if (!this._pointerHeld) return;
    event.preventDefault();
    this._pointerHeld = false;
    if (this._keysHeld.size === 0) this._push('release', 'pointer');
  }

  _onPointerMove(event) {
    if (!this._pointerHeld) return;
    // 1 agitation unit ≈ 40 px of rubbing.
    const moved = Math.hypot(event.clientX - this._lastX, event.clientY - this._lastY);
    this._agitation += moved / 40;
    this._lastX = event.clientX;
    this._lastY = event.clientY;
  }

  _onKeyDown(event) {
    const isHoldKey = ['Space', 'Enter'].includes(event.code);
    const held = this._pointerHeld || this._keysHeld.size > 0;

    if (!isHoldKey) {
      // Any other key mashed while holding = keyboard scrub agitation.
      if (held && !event.repeat) this._agitation += 1;
      return;
    }

    event.preventDefault();
    if (event.repeat || this._keysHeld.has(event.code)) return;
    this._keysHeld.add(event.code);
    if (held) this._agitation += 1; // second hold key doubles as agitation
    else this._push('press', 'keyboard');
  }

  _onKeyUp(event) {
    if (!['Space', 'Enter'].includes(event.code)) return;
    event.preventDefault();
    if (!this._keysHeld.has(event.code)) return;
    this._keysHeld.delete(event.code);
    if (!this._pointerHeld && this._keysHeld.size === 0) this._push('release', 'keyboard');
  }
}
